import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth';
import { LoadingSpinner } from '@/shared/components';
import { CheckCircle, XCircle, AlertCircle, Clock, Trophy, RotateCcw } from 'lucide-react';
import { complianceTestService, type Test, type TestResult } from '../services/compliance-test.service';

export default function OnboardingStep6() {
    const [loading, setLoading] = useState(true);
    const [test, setTest] = useState<Test | null>(null);
    const [selectedAnswers, setSelectedAnswers] = useState<Record<string, string>>({});
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState<TestResult | null>(null);
    const [error, setError] = useState('');
    const [startTime] = useState(Date.now());
    const navigate = useNavigate();
    const { user, refreshUser } = useAuth();

    useEffect(() => {
        loadTest();
    }, []);

    const loadTest = async () => {
        try {
            setLoading(true);
            setError('');
            const testData = await complianceTestService.getMyTest();
            setTest(testData);
            setSelectedAnswers({});
            setResult(null);
        } catch (err: any) {
            console.error('Error loading test:', err);
            setError(err.response?.data?.error || 'Failed to load compliance test');
        } finally {
            setLoading(false);
        }
    };

    const handleAnswerSelect = (questionId: string, answer: string) => {
        setSelectedAnswers(prev => ({
            ...prev,
            [questionId]: answer,
        }));
    };

    const handleSubmit = async () => {
        if (!test) return;

        // Validate all questions answered
        const unansweredCount = test.questions.length - Object.keys(selectedAnswers).length;
        if (unansweredCount > 0) {
            setError(`Please answer all questions. ${unansweredCount} question(s) remaining.`);
            return;
        }

        try {
            setSubmitting(true);
            setError('');

            const timeSpent = Math.floor((Date.now() - startTime) / 1000);

            const submitData = {
                testId: test.id,
                answers: Object.entries(selectedAnswers).map(([questionId, selectedAnswer]) => ({
                    questionId,
                    selectedAnswer,
                })),
                timeSpent,
            };

            const testResult = await complianceTestService.submitTest(submitData);
            setResult(testResult);

            // Refresh user data if passed
            if (testResult.passed) {
                await refreshUser();
            }
        } catch (err: any) {
            console.error('Error submitting test:', err);
            setError(err.response?.data?.error || 'Failed to submit test');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRetake = () => {
        setResult(null);
        setSelectedAnswers({});
        setError('');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleContinue = async () => {
        await refreshUser();
        // Navigate to role-based dashboard
        if (user?.role === 'agent') {
            navigate('/agent/dashboard');
        } else {
            navigate('/user/dashboard');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
                <LoadingSpinner size="lg" />
            </div>
        );
    }

    if (!test) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
                <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
                    <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Test Not Available</h2>
                    <p className="text-gray-600 mb-4">{error || 'No compliance test found for your account.'}</p>
                    <button
                        onClick={() => {
                            if (user?.role === 'agent') {
                                navigate('/agent/dashboard');
                            } else {
                                navigate('/user/dashboard');
                            }
                        }}
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Go to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    // Show results screen
    if (result) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-8 px-4">
                <div className="max-w-4xl mx-auto">
                    {/* Results Header */}
                    <div className={`rounded-xl shadow-lg p-8 mb-6 ${
                        result.passed ? 'bg-gradient-to-r from-emerald-500 to-teal-500' : 'bg-gradient-to-r from-red-500 to-orange-500'
                    }`}>
                        <div className="text-center text-white">
                            {result.passed ? (
                                <Trophy className="w-20 h-20 mx-auto mb-4" />
                            ) : (
                                <XCircle className="w-20 h-20 mx-auto mb-4" />
                            )}
                            <h1 className="text-4xl font-bold mb-2">
                                {result.passed ? 'Congratulations!' : 'Not Quite There'}
                            </h1>
                            <p className="text-xl mb-4">
                                {result.passed
                                    ? 'You passed the compliance test!'
                                    : 'You can retake the test as many times as needed'}
                            </p>
                            <div className="flex items-center justify-center space-x-8 text-2xl font-bold">
                                <div>
                                    <div className="text-5xl">{result.score}%</div>
                                    <div className="text-sm font-normal">Your Score</div>
                                </div>
                                <div>
                                    <div className="text-5xl">{result.passingScore}%</div>
                                    <div className="text-sm font-normal">Required</div>
                                </div>
                                <div>
                                    <div className="text-5xl">{result.correctAnswers}/{result.totalQuestions}</div>
                                    <div className="text-sm font-normal">Correct</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="bg-white rounded-xl shadow-lg p-6 mb-6 text-center">
                        {result.passed ? (
                            <div>
                                <p className="text-lg text-gray-700 mb-4">
                                    You are now <span className="font-bold text-emerald-600">Ready to Sell</span>!
                                </p>
                                <button
                                    onClick={handleContinue}
                                    className="bg-emerald-600 text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-emerald-700 transition-colors shadow-lg"
                                >
                                    Continue to Dashboard
                                </button>
                            </div>
                        ) : (
                            <div>
                                <p className="text-lg text-gray-700 mb-4">
                                    Review the answers below and try again when you're ready.
                                </p>
                                <button
                                    onClick={handleRetake}
                                    className="bg-blue-600 text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg inline-flex items-center space-x-2"
                                >
                                    <RotateCcw className="w-5 h-5" />
                                    <span>Retake Test</span>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Answer Review */}
                    <div className="bg-white rounded-xl shadow-lg p-6">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Answer Review</h2>
                        <div className="space-y-6">
                            {result.answers.map((answer, index) => (
                                <div
                                    key={answer.questionId}
                                    className={`p-4 rounded-lg border-2 ${
                                        answer.isCorrect
                                            ? 'border-emerald-200 bg-emerald-50'
                                            : 'border-red-200 bg-red-50'
                                    }`}
                                >
                                    <div className="flex items-start justify-between mb-2">
                                        <h3 className="font-semibold text-gray-900 flex-1">
                                            {index + 1}. {answer.questionText}
                                        </h3>
                                        {answer.isCorrect ? (
                                            <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0 ml-2" />
                                        ) : (
                                            <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 ml-2" />
                                        )}
                                    </div>
                                    <div className="space-y-2 text-sm">
                                        <div>
                                            <span className="font-medium">Your answer: </span>
                                            <span className={answer.isCorrect ? 'text-emerald-700' : 'text-red-700'}>
                                                {answer.selectedAnswer}
                                            </span>
                                        </div>
                                        {!answer.isCorrect && (
                                            <div>
                                                <span className="font-medium">Correct answer: </span>
                                                <span className="text-emerald-700">{answer.correctAnswer}</span>
                                            </div>
                                        )}
                                        {answer.explanation && (
                                            <div className="mt-2 p-3 bg-white rounded border border-gray-200">
                                                <span className="font-medium text-gray-700">Explanation: </span>
                                                <span className="text-gray-600">{answer.explanation}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Show test
    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-8 px-4">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">{test.name}</h1>
                            {test.description && (
                                <p className="text-gray-600 mt-2">{test.description}</p>
                            )}
                        </div>
                        {test.timeLimit && (
                            <div className="flex items-center space-x-2 text-blue-600">
                                <Clock className="w-5 h-5" />
                                <span className="font-semibold">{test.timeLimit} min</span>
                            </div>
                        )}
                    </div>
                    <div className="mt-4 flex items-center space-x-6 text-sm text-gray-600">
                        <div>
                            <span className="font-semibold">{test.totalQuestions}</span> questions
                        </div>
                        <div>
                            Passing score: <span className="font-semibold">{test.passingScore}%</span>
                        </div>
                        <div className="text-green-600 font-medium">
                            Unlimited attempts
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6">
                        {error}
                    </div>
                )}

                {/* Questions */}
                <div className="space-y-6 mb-6">
                    {test.questions.map((question, index) => (
                        <div key={question.id} className="bg-white rounded-xl shadow-lg p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                {index + 1}. {question.questionText}
                            </h3>
                            <div className="space-y-3">
                                {question.options.map((option) => (
                                    <label
                                        key={option}
                                        className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                                            selectedAnswers[question.id] === option
                                                ? 'border-blue-500 bg-blue-50'
                                                : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        <input
                                            type="radio"
                                            name={question.id}
                                            value={option}
                                            checked={selectedAnswers[question.id] === option}
                                            onChange={(e) => handleAnswerSelect(question.id, e.target.value)}
                                            className="w-4 h-4 text-blue-600 mr-3"
                                        />
                                        <span className="text-gray-800">{option}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Submit Button */}
                <div className="bg-white rounded-xl shadow-lg p-6 sticky bottom-4">
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-600">
                            <span className="font-semibold">{Object.keys(selectedAnswers).length}/{test.totalQuestions}</span> questions answered
                        </div>
                        <button
                            onClick={handleSubmit}
                            disabled={submitting || Object.keys(selectedAnswers).length !== test.totalQuestions}
                            className={`px-8 py-3 rounded-lg text-lg font-semibold transition-colors ${
                                submitting || Object.keys(selectedAnswers).length !== test.totalQuestions
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg'
                            }`}
                        >
                            {submitting ? 'Submitting...' : 'Submit Test'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
